import { useState, useCallback } from 'react';
import { comedorComensalApi } from '../api/comedorComensalApi';
import { handleApiError } from '../utils/errorHandler';
import { toast } from 'react-toastify';

export const useComedorComensal = () => {
  const [comedoresAsignados, setComedoresAsignados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Obtener comedores asignados a un comensal
  const fetchComedoresAsignados = useCallback(async (comensalId) => {
    if (!comensalId) {
      setComedoresAsignados([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const comedores = await comedorComensalApi.getComedoresByComensal(comensalId);
      setComedoresAsignados(comedores);
      return comedores;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Error al cargar comedores asignados');
      setError(errorMessage);
      console.error('Error al cargar comedores asignados:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Asignar un comedor a un comensal
  const asignarComedor = useCallback(async (comensalId, comedorId) => {
    if (!comensalId || !comedorId) {
      toast.error('Datos incompletos para asignar comedor');
      return false;
    }

    setLoading(true);
    try {
      await comedorComensalApi.asignarComedor(comensalId, comedorId);
      
      // Actualizar la lista local
      await fetchComedoresAsignados(comensalId);
      
      toast.success('Comedor asignado exitosamente', {
        position: "top-right",
        autoClose: 2000
      });
      return true;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Error al asignar comedor');
      toast.error(`Error al asignar comedor: ${errorMessage}`, {
        position: "top-right",
        autoClose: 3000
      });
      console.error('Error al asignar comedor:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchComedoresAsignados]);

  // Desasignar un comedor de un comensal
  const desasignarComedor = useCallback(async (comensalId, comedorId) => {
    if (!comensalId || !comedorId) {
      toast.error('Datos incompletos para desasignar comedor');
      return false;
    }

    setLoading(true);
    try {
      await comedorComensalApi.desasignarComedor(comensalId, comedorId);
      
      // Actualizar la lista local
      await fetchComedoresAsignados(comensalId);
      
      toast.success('Comedor desasignado exitosamente', {
        position: "top-right",
        autoClose: 2000
      });
      return true;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Error al desasignar comedor');
      toast.error(`Error al desasignar comedor: ${errorMessage}`, {
        position: "top-right",
        autoClose: 3000
      });
      console.error('Error al desasignar comedor:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchComedoresAsignados]);

  // Actualizar todas las asignaciones de comedores
  const updateComedoresAsignados = useCallback(async (comensalId, comedoresIds) => {
    if (!comensalId) {
      toast.error('ID de comensal requerido');
      return false;
    }

    setLoading(true);
    try {
      await comedorComensalApi.updateComedoresAsignados(comensalId, comedoresIds || []);
      
      // Actualizar la lista local
      await fetchComedoresAsignados(comensalId);
      
      toast.success('Asignaciones de comedores actualizadas exitosamente', {
        position: "top-right",
        autoClose: 2000
      });
      return true;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Error al actualizar asignaciones');
      toast.error(`Error al actualizar asignaciones: ${errorMessage}`, {
        position: "top-right",
        autoClose: 3000
      });
      console.error('Error al actualizar asignaciones:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchComedoresAsignados]);

  // Agregar comedor al estado local (para comensales nuevos)
  const addComedorLocal = useCallback((comedor) => {
    setComedoresAsignados(prev => {
      // Verificar si ya existe
      const exists = prev.some(c => c.id === comedor.id);
      if (exists) {
        return prev;
      }
      return [...prev, comedor];
    });
  }, []);

  // Remover comedor del estado local (para comensales nuevos)
  const removeComedorLocal = useCallback((comedorId) => {
    setComedoresAsignados(prev => prev.filter(c => c.id !== comedorId));
  }, []);

  // Limpiar estado
  const clearComedoresAsignados = useCallback(() => {
    setComedoresAsignados([]);
    setError(null);
  }, []);

  return {
    comedoresAsignados,
    loading,
    error,
    fetchComedoresAsignados,
    asignarComedor,
    desasignarComedor,
    updateComedoresAsignados,
    addComedorLocal,
    removeComedorLocal,
    clearComedoresAsignados
  };
};
